import {ApgPortfolio} from '../model/ApgPortfolio';
import {ServiceConfiguration} from './ServiceConfiguration';

// THE GROUPED PORTFOLIO LIST — ONE implementation (CR-20260910-001, G3 defect 1, Alan's ruling).
//
// ⚠ WHY THIS FILE EXISTS. This list is currently copy-pasted into four panels — the APG, the
// Hedging Grid's selector, the OPG and Package Comparison — each with its own copy of the
// add-self special case. Alan ruled ONE implementation. Maven is its first consumer; **the four
// existing copies are deliberately NOT modified** by this change, so the reuse arrives without
// touching four shipped panels at gate G3. They can adopt it later, one at a time.
//
// It lives beside the portfolios service because the model and the service already live here,
// which means a consumer importing it takes on no new module dependency.
//
// ⚠ THE SPECIAL CASE THE COPIES ALL CARRY, WRITTEN DOWN HERE ONCE:
// `sessionData.users` DOES NOT CONTAIN THE LOGGED-IN USER. Every copy adds self separately, and
// a super-user path that only loops `users` silently shows every trader's portfolios EXCEPT the
// trader's own — which is precisely the defect Alan hit at G3.
//
// ⚠ AND THE SECOND ONE: the server does not supply a display name for SELF. The other users
// arrive already formatted; self's is synthesized locally as first name plus last initial
// ("Alan K."). A group header built from the raw session data is headerless or wrong.
//
// ⚠ AND THE ONE THE EXISTING COPIES GET WRONG: they ASSUME the server never returns self in
// `users`. If it ever did, the loop would build one group for self and the add-self would build
// another, and nothing dedupes — two groups, same trader, same portfolios. This implementation is
// immune by construction (see `isSelf` below). That assumption is exactly the kind the old code
// carried without writing down.
//
// CR-20260928-001: the `<No Portfolio>` scratchpad is gone. Every portfolio is real; the DEFAULT
// one is an ordinary portfolio flagged `isDefault` (boot/fallback role only). Two traders' defaults
// may still both read "Default Portfolio" — the owner on the closed box is what tells them apart,
// exactly as before.
//
// ES5 note: arrays + indexOf only.

// One group, in the shape dx-select-box's `[grouped]="true"` consumes: `key` is the header text.
export interface PortfolioGroup {
    key: string;
    userId: string;
    items: ApgPortfolio[];
}

// Only the session facts this needs, so the helper takes no Angular service and is unit-testable
// directly.
export interface PortfolioGroupsSession {
    userId: string;
    firstName: string;
    lastName: string;
    users: { userId: string; userName: string }[];
}

// Supplied by the caller so this file depends on no service instance: the caller configures its
// own portfolios service for that identity and returns what it reads.
export type PortfolioReader = (cfg: ServiceConfiguration) => ApgPortfolio[];

// Self's display name, as every panel in this app synthesizes it: first name plus last initial.
// The server supplies no name for self.
//
// Defensive on a missing or empty last name — `lastName[0]` is `undefined` on an empty string and
// THROWS on an undefined one, which would take the whole panel down at open. The normal output is
// unchanged; only the degenerate input differs.
export function makeOwnDisplayName(session: PortfolioGroupsSession): string {
    if (!session) {
        return '';
    }
    const first = session.firstName || '';
    const last = session.lastName || '';
    const initial = last.length > 0 ? ' ' + last.charAt(0) + '.' : '';
    return (first + initial).trim();
}

// Stamp the owner onto each portfolio.
//
// TWO REASONS, and the second is the one that matters. (1) The closed box reads
// "Alan K.  |  Default Portfolio", which is what makes two default entries tellable apart. (2) THE
// SELECTED PORTFOLIO CARRIES ITS OWN OWNER, so a consumer putting an identity on the wire reads
// it from the portfolio rather than from the session — which is ruling R-E, and the reason a
// super user may run on another trader's portfolio at all.
function withOwner(portfolios: ApgPortfolio[], userId: string, userName: string): ApgPortfolio[] {
    const source = portfolios || [];
    const out: ApgPortfolio[] = [];
    for (let i = 0; i < source.length; i++) {
        const p = source[i];
        out.push({
            id: p.id,
            name: p.name,
            asset: p.asset,
            strategy: p.strategy,
            // The portfolio's own owner wins if it already carries one; the group's identity is
            // the fallback, never an override.
            userId: p.userId || userId,
            userName: p.userName || userName,
            isIndependent: p.isIndependent,
            defaultQty: p.defaultQty,
            isDefault: p.isDefault
        });
    }
    return out;
}

/**
 * Build the grouped portfolio list.
 *
 * ORDER IS PART OF THE CONTRACT (Alan's acceptance criteria 2 and 3): the logged-in user's own
 * group comes FIRST, then the other traders sorted by name. A super user opens this list to work
 * on his own book most of the time; alphabetical-including-self would bury it.
 *
 * A non-super user gets exactly one group — his own — which is the path that already worked and
 * must not regress.
 */
export function buildPortfolioGroups(
    session: PortfolioGroupsSession,
    isSuperUser: boolean,
    readPortfolios: PortfolioReader
): PortfolioGroup[] {

    if (!session || !readPortfolios) {
        return [];
    }

    const ownName = makeOwnDisplayName(session);

    const ownGroup: PortfolioGroup = {
        key: ownName,
        userId: session.userId,
        items: withOwner(
            readPortfolios({orientation: undefined, userId: session.userId, userName: ownName}),
            session.userId,
            ownName)
    };

    if (!isSuperUser) {
        // The path that already works. One group, the trader's own — and it still carries a
        // header, so the surface does not change shape between an ordinary trader and a super
        // user with one colleague.
        return [ownGroup];
    }

    const others = session.users || [];

    // ⚠ THE DEDUPE. `users` is documented not to contain self, and every existing copy trusts
    // that. If the server ever changes its mind, this drops the duplicate instead of rendering
    // the same trader twice — and a trader seeing his own portfolios in two places would have no
    // way to know which one he picked.
    const filtered: { userId: string; userName: string }[] = [];
    for (let i = 0; i < others.length; i++) {
        const u = others[i];
        if (u && u.userId && u.userId !== session.userId) {
            filtered.push(u);
        }
    }

    const sorted = filtered.slice().sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));

    const groups: PortfolioGroup[] = [ownGroup];
    for (let i = 0; i < sorted.length; i++) {
        const u = sorted[i];
        groups.push({
            key: u.userName,
            userId: u.userId,
            items: withOwner(
                readPortfolios({orientation: undefined, userId: u.userId, userName: u.userName}),
                u.userId,
                u.userName)
        });
    }

    return groups;
}
